import { Link } from "react-router-dom";

const formatShot = (made, attempted) => `${made ?? 0}/${attempted ?? 0}`;

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatPercent = (percent, made, attempted) => {
    if (percent !== undefined && percent !== null && percent !== "") {
        return `${percent}%`;
    }

    const attemptedValue = toNumber(attempted);
    if (attemptedValue === 0) {
        return "0%";
    }

    return `${Math.round((toNumber(made) / attemptedValue) * 100)}%`;
};

const getTotal = (totals, primaryKey, alternativeKey) => {
    if (totals?.[primaryKey] !== undefined) {
        return totals[primaryKey];
    }
    if (alternativeKey && totals?.[alternativeKey] !== undefined) {
        return totals[alternativeKey];
    }
    return 0;
};

function PartidoStatsTable({
    title,
    players = [],
    totals = {},
    isAguadaTeam = false,
    accent = "green",
    minutes = "-",
    coach = "-",
    onPlayerClick,
}) {
    const accentClass = accent === "red" ? "bg-red-500" : "bg-green-500";
    const accentTextClass = accent === "red" ? "text-red-400" : "text-green-400";
    const accentHoverClass = accent === "red" ? "hover:text-red-300" : "hover:text-green-300";
    const totalRowClass = accent === "red" ? "bg-red-500/30" : "bg-green-500/30";
    const thickRightBorderClass = "!border-r-2 !border-white/20";
    const showNumero = players.some((p) => p.numero !== undefined && p.numero !== null);
    const showMasMenos = players.some((p) => p.masMenos !== undefined && p.masMenos !== null);

    return (
        <section className="bg-[#102216] rounded-xl p-3 sm:p-4 md:p-6 border border-white/10">
            <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className={`w-1.5 sm:w-2 h-6 sm:h-8 ${accentClass} rounded-full shrink-0`}></div>
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-100 truncate">{title}</h2>
                </div>
                <span className="text-xs sm:text-sm text-slate-400 font-semibold shrink-0">
                    DT:{" "}
                    {isAguadaTeam && coach ? (
                        <Link
                            to={`/jugador/${encodeURIComponent(coach)}?tipo=tecnico`}
                            className={`text-white transition-colors ${accentHoverClass}`}
                        >
                            {coach}
                        </Link>
                    ) : (
                        coach || "-"
                    )}
                </span>
            </div>

            <div className="-mx-3 sm:mx-0 overflow-x-auto rounded-none sm:rounded-xl border-y sm:border border-white/10 shadow-xl scrollbar-hide sm:[&]:scrollbar-default">
                <table className="w-full text-[10px] sm:text-xs md:text-sm whitespace-nowrap text-slate-100 [&_th]:text-center [&_td]:text-center [&_th]:border-r [&_th]:border-white/10 [&_td]:border-r [&_td]:border-white/10 [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0 [&_thead_th]:text-slate-400 [&_thead_th]:font-semibold [&_thead_th]:uppercase [&_thead_th]:tracking-wide">
                    <thead>
                        <tr className="bg-[#1b2a22] border-b border-white/10">
                            {showNumero && <th className="px-2 sm:px-3 py-2 sm:py-3">N°</th>}
                            <th className="px-2 sm:px-4 py-2 sm:py-3 sticky left-0 z-20 bg-[#1b2a22] text-left min-w-[100px] sm:min-w-[140px]">Jugador</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">MIN</th>
                            <th className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>PTS</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">LC</th>
                            <th className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>LC%</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">2PTS</th>
                            <th className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>2PTS%</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">3PTS</th>
                            <th className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>3PTS%</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">1PTS</th>
                            <th className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>1PTS%</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">RO</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">RD</th>
                            <th className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>RT</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">AS</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">PER</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">ROB</th>
                            <th className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>BLQ</th>
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">FP</th>
                            <th className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>FR</th>
                            {showMasMenos && <th className="px-1.5 sm:px-2 py-2 sm:py-3">+/-</th>}
                            <th className="px-1.5 sm:px-2 py-2 sm:py-3">EF</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                        {players.map((player) => (
                            <tr key={`${title}-${player.jugador}`} className="group bg-[#042114] hover:bg-[#0b2b1d] transition-colors">
                                {showNumero && <td className="px-2 sm:px-3 py-2 sm:py-3 text-center">{player.numero ?? "-"}</td>}
                                <td className="px-2 sm:px-4 py-2 sm:py-3 font-bold sticky left-0 z-10 bg-[#042114] group-hover:bg-[#0b2b1d] transition-colors text-left">
                                    {onPlayerClick ? (
                                        <button
                                            type="button"
                                            onClick={() => onPlayerClick(player.jugador)}
                                            className={`transition-colors text-white ${accentHoverClass} text-left`}
                                        >
                                            {player.jugador}
                                        </button>
                                    ) : isAguadaTeam ? (
                                        <Link to={`/jugador/${encodeURIComponent(player.jugador)}`} className={`transition-colors text-white ${accentHoverClass}`}>
                                            {player.jugador}
                                        </Link>
                                    ) : (
                                        player.jugador
                                    )}
                                </td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{player.minutos ?? 0}</td>
                                <td className={`px-1.5 sm:px-2 py-2 sm:py-3 font-bold ${accentTextClass} ${thickRightBorderClass}`}>{player.puntos ?? 0}</td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{formatShot(player.camposHechos ?? (player.doblesHechos ?? 0) + (player.triplesHechos ?? 0), player.camposIntent ?? (player.doblesIntent ?? 0) + (player.triplesIntent ?? 0))}</td>
                                <td className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>{formatPercent(player.camposPercent, player.camposHechos ?? (player.doblesHechos ?? 0) + (player.triplesHechos ?? 0), player.camposIntent ?? (player.doblesIntent ?? 0) + (player.triplesIntent ?? 0))}</td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{formatShot(player.doblesHechos, player.doblesIntent)}</td>
                                <td className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>{formatPercent(player.doblesPercent, player.doblesHechos, player.doblesIntent)}</td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{formatShot(player.triplesHechos, player.triplesIntent)}</td>
                                <td className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>{formatPercent(player.triplesPercent, player.triplesHechos, player.triplesIntent)}</td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{formatShot(player.libresHechos, player.libresIntent)}</td>
                                <td className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>{formatPercent(player.libresPercent, player.libresHechos, player.libresIntent)}</td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{player.rebotesOff ?? 0}</td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{player.rebotesDef ?? 0}</td>
                                <td className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>{player.rebotesTot ?? 0}</td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{player.asistencias ?? 0}</td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{player.perdidas ?? 0}</td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{player.robos ?? 0}</td>
                                <td className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>{player.tapas ?? 0}</td>
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{player.fouls ?? 0}</td>
                                <td className={`px-1.5 sm:px-2 py-2 sm:py-3 ${thickRightBorderClass}`}>{player.foulsRec ?? 0}</td>
                                {showMasMenos && <td className="px-1.5 sm:px-2 py-2 sm:py-3">{player.masMenos ?? "-"}</td>}
                                <td className="px-1.5 sm:px-2 py-2 sm:py-3">{player.valor ?? "-"}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot className={`${totalRowClass} font-bold text-slate-100`}>
                        <tr>
                            {showNumero && <td className="px-2 sm:px-3 py-3 sm:py-4 bg-[#102216]"></td>}
                            <td className="px-2 sm:px-4 py-3 sm:py-4 text-left sticky left-0 z-20 bg-[#102216]">TOTAL EQUIPO</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4 bg-[#102216]">{minutes}</td>
                            <td className={`px-1.5 sm:px-2 py-3 sm:py-4 ${accentTextClass} ${thickRightBorderClass}`}>{totals.totalPuntos ?? 0}</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{formatShot(getTotal(totals, "totalCamposHechos"), getTotal(totals, "totalCamposIntent", "totalCamposIntentados"))}</td>
                            <td className={`px-1.5 sm:px-2 py-3 sm:py-4 ${thickRightBorderClass}`}>{formatPercent(totals.totalCamposPercent, getTotal(totals, "totalCamposHechos"), getTotal(totals, "totalCamposIntent", "totalCamposIntentados"))}</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{formatShot(getTotal(totals, "totalDoblesHechos"), getTotal(totals, "totalDoblesIntentados", "totalDoblesIntent"))}</td>
                            <td className={`px-1.5 sm:px-2 py-3 sm:py-4 ${thickRightBorderClass}`}>{formatPercent(totals.totalDoblesPercent, getTotal(totals, "totalDoblesHechos"), getTotal(totals, "totalDoblesIntentados", "totalDoblesIntent"))}</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{formatShot(getTotal(totals, "totalTriplesHechos"), getTotal(totals, "totalTriplesIntentados", "totalTriplesIntent"))}</td>
                            <td className={`px-1.5 sm:px-2 py-3 sm:py-4 ${thickRightBorderClass}`}>{formatPercent(totals.totalTriplesPercent, getTotal(totals, "totalTriplesHechos"), getTotal(totals, "totalTriplesIntentados", "totalTriplesIntent"))}</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{formatShot(getTotal(totals, "totalLibresHechos"), getTotal(totals, "totalLibresIntentados", "totalLibresIntent"))}</td>
                            <td className={`px-1.5 sm:px-2 py-3 sm:py-4 ${thickRightBorderClass}`}>{formatPercent(totals.totalLibresPercent, getTotal(totals, "totalLibresHechos"), getTotal(totals, "totalLibresIntentados", "totalLibresIntent"))}</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{totals.totalRebotesOff ?? 0}</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{totals.totalRebotesDef ?? 0}</td>
                            <td className={`px-1.5 sm:px-2 py-3 sm:py-4 ${thickRightBorderClass}`}>{totals.totalRebotesTot ?? 0}</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{totals.totalAsistencias ?? 0}</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{totals.totalPerdidas ?? 0}</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{totals.totalRobos ?? 0}</td>
                            <td className={`px-1.5 sm:px-2 py-3 sm:py-4 ${thickRightBorderClass}`}>{totals.totalTapas ?? 0}</td>
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{totals.totalFouls ?? 0}</td>
                            <td className={`px-1.5 sm:px-2 py-3 sm:py-4 ${thickRightBorderClass}`}>{totals.totalFoulsRec ?? 0}</td>
                            {showMasMenos && <td className="px-1.5 sm:px-2 py-3 sm:py-4">{totals.totalMasMenos ?? "-"}</td>}
                            <td className="px-1.5 sm:px-2 py-3 sm:py-4">{totals.totalValor ?? "-"}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </section>
    );
}

export default PartidoStatsTable;
